import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker, type DateRange } from 'react-day-picker';
import {
  format,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import 'react-day-picker/dist/style.css';

// Type for custom date range (start and end dates)
export interface DateRangeValue {
  startDate: string; // YYYY-MM-DD format
  endDate: string;   // YYYY-MM-DD format
}

interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

// Helper to get today's date in Israel timezone as YYYY-MM-DD
export function getTodayIL(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

// Helper to get date N days ago in Israel timezone
export function getDaysAgoIL(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

// Default date range (last 30 days)
export function getDefaultDateRange(): DateRangeValue {
  return { startDate: getDaysAgoIL(30), endDate: getTodayIL() };
}

const ymd = (d: Date) => format(d, 'yyyy-MM-dd');
const parseYMD = (s: string) => {
  // Local-tz midnight parse — avoids the off-by-one you get from `new Date('YYYY-MM-DD')`.
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

interface Preset {
  key: string;
  label: string;
  range: () => { from: Date; to: Date };
}

function buildPresets(t: (k: string) => string): Preset[] {
  const today = new Date();
  return [
    { key: 'today',     label: t('dateRange.today'),      range: () => ({ from: today, to: today }) },
    { key: 'yesterday', label: t('dateRange.yesterday'),  range: () => { const y = subDays(today, 1); return { from: y, to: y }; } },
    { key: 'last7',     label: t('dateRange.last7Days'),  range: () => ({ from: subDays(today, 6), to: today }) },
    { key: 'last30',    label: t('dateRange.last30Days'), range: () => ({ from: subDays(today, 29), to: today }) },
    { key: 'thisMonth', label: t('dateRange.thisMonth'),  range: () => ({ from: startOfMonth(today), to: today }) },
    { key: 'lastMonth', label: t('dateRange.lastMonth'),  range: () => { const lm = subMonths(today, 1); return { from: startOfMonth(lm), to: endOfMonth(lm) }; } },
  ];
}

export default function DateRangeSelector({
  value,
  onChange,
}: DateRangeSelectorProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // Draft holds the in-progress selection; only committed on "Apply" (or a preset click).
  const [draft, setDraft] = useState<DateRangeValue>(value);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isRtl = i18n.language === 'he';

  // Whenever the popover opens, sync the draft to the current committed value.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  // Close popover on outside click (discards the draft).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Presets bypass the draft — they apply immediately (1 click).
  const applyPreset = (from: Date, to: Date) => {
    onChange({ startDate: ymd(from), endDate: ymd(to) });
    setOpen(false);
  };

  // Apply the draft on explicit Apply click.
  const applyDraft = () => {
    onChange(draft);
    setOpen(false);
  };

  // From-input change — keep To if it's still ≥ From, else collapse to start.
  const onFromInput = (s: string) => {
    if (!s) return;
    const newEnd = s > draft.endDate ? s : draft.endDate;
    setDraft({ startDate: s, endDate: newEnd });
  };

  // To-input change — keep From if it's still ≤ To, else collapse to end.
  const onToInput = (s: string) => {
    if (!s) return;
    const newStart = s < draft.startDate ? s : draft.startDate;
    setDraft({ startDate: newStart, endDate: s });
  };

  // Calendar selection updates the draft; doesn't apply until Apply is clicked.
  const onCalendar = (r: DateRange | undefined) => {
    if (!r?.from) return;
    setDraft({
      startDate: ymd(r.from),
      endDate: r.to ? ymd(r.to) : ymd(r.from),
    });
  };

  const calendarSelected: DateRange = {
    from: parseYMD(draft.startDate),
    to: parseYMD(draft.endDate),
  };

  const buttonLabel =
    value.startDate === value.endDate
      ? format(parseYMD(value.startDate), 'd MMM yyyy')
      : `${format(parseYMD(value.startDate), 'd MMM')} – ${format(
          parseYMD(value.endDate),
          'd MMM yyyy'
        )}`;

  const presets = buildPresets(t);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      >
        <CalendarIcon className="h-4 w-4 text-gray-500" />
        <span>{buttonLabel}</span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 flex rounded-md border bg-white shadow-lg">
          {/* Presets column — 1-click apply */}
          <div
            className={`flex min-w-[140px] flex-col gap-1 p-2 ${
              isRtl ? 'border-l' : 'border-r'
            } border-gray-200`}
          >
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  const r = p.range();
                  applyPreset(r.from, r.to);
                }}
                className="rounded px-3 py-1.5 text-start text-sm text-gray-700 hover:bg-gray-100"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom range column — From/To inputs + calendar + Apply */}
          <div className="flex flex-col p-2" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2">
              <label className="text-xs text-gray-500">{t('dateRange.from')}</label>
              <input
                type="date"
                value={draft.startDate}
                max={draft.endDate}
                onChange={(e) => onFromInput(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <label className="text-xs text-gray-500">{t('dateRange.to')}</label>
              <input
                type="date"
                value={draft.endDate}
                min={draft.startDate}
                onChange={(e) => onToInput(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <DayPicker
              mode="range"
              selected={calendarSelected}
              onSelect={onCalendar}
              numberOfMonths={2}
              showOutsideDays
            />

            <div className="mt-2 flex items-center justify-end gap-2 border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={applyDraft}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
              >
                {t('dateRange.apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
